import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useIntl } from "react-intl";
import {
  AppContext,
  SendByteWallet,
  SharedContextProps,
  TableHeaderFilter,
} from "@lib/types";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiGetSendByteWallets } from "@lib/api/walletSendByte";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import {
  AppTable,
  AppTableContentHeader,
} from "@lib/components/MiniComponents/Table";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import getColumns from "./columns";
import { useSendByteWalletQueries } from "@lib/apiQueries/useSendByteWalletQueries";
import { SendbyteWalletModal } from "@lib/components/PageComponents/Wallet/WalletModal";

const SharedWalletAddressSendByteList: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  customerId,
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
}) => {
  const navigate = useNavigate();
  const { messages } = useIntl();

  const filter = [].concat(
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  );

  const {
    params,
    setParams,
    handleRemoveFilters: handleRemoveFiltersParams,
    onPageChange,
  } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    filter: filter,
    join: ["customer"],
    isShowParamsInUrl: false,
  });

  const enabled = !!params && !!pageIsActive;
  const sendByteWalletsQuery = useQuery({
    queryFn: () => apiGetSendByteWallets(params),
    queryKey: ["sendbyte-wallets", JSON.stringify(params)],
    enabled: enabled,
  });
  const { data, isLoading, isFetching } = sendByteWalletsQuery;
  const apiData = data?.data?.data;

  const {
    addSendByteWalletMutation,
    updateSendByteWalletMutation,
    deleteSendByteWalletMutation,
  } = useSendByteWalletQueries({
    messages,
  });

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<SendByteWallet>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleRemoveFilter = (filter: string[]) => {
    handleRemoveFiltersParams(filter);
  };

  const handleSelect = (wallet: SendByteWallet) => {
    setSelected(wallet);
    toggle(true);
  };

  const onChange: TableProps<SendByteWallet>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        apiData,
        deleteQuery: deleteSendByteWalletMutation,
        openModal,
        handleSelect,
        params,
        messages,
        navigate,
      }),
    [apiData, deleteSendByteWalletMutation, openModal, handleSelect, params],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["customerId"];
    }
    return [];
  }, [context, detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params, columns]);

  // Context-specific title and breadcrumb
  const title = customTitle || (messages["common.sendByteWallets"] as string);

  return (
    <AppsContainer
      noContainer={!!withoutContainer}
      title={title}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 100}
          onPageChange={onPageChange}
          query={sendByteWalletsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilter}
          plusProps={
            context === AppContext.CUSTOMER_LEAD && detailContext
              ? undefined
              : {
                  onClick: () => toggle(true),
                  disabled: isLoading,
                  loading: isLoading,
                }
          }
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching}
          minusHeight={20}
        />

        <SendbyteWalletModal
          mutationQuery={
            selected ? updateSendByteWalletMutation : addSendByteWalletMutation
          }
          walletQuery={sendByteWalletsQuery}
          openModal={openModal}
          toggleModal={toggle}
          wallet={selected}
          customerId={customerId}
        />
      </AppsContent>
    </AppsContainer>
  );
};

export default SharedWalletAddressSendByteList;
